import { Col, Row, Alert, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { AccountsController } from "../../../api/src/accounts/accounts.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import type {
  RebalanceAccount,
  RebalanceMetrics,
  RebalanceType,
} from "../../../api/src/rebalances/rebalances.service";
import Loading from "../Loading";
import { useQueryAccount } from "../clients/account/lib";
import AllocationBar from "../components/AllocationBar/AllocationBar";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../components/AssetAllocationBarChart";
import { useAuthenticatedFetch } from "../lib/api";
import { naLabel } from "../lib/display";
import { formatCurrency } from "../lib/numbers";
import { useQueryModel } from "../models/lib";
import { getRiskAllocation } from "./lib";

export type RebalanceAccountWithMetrics = RebalanceAccount &
  Required<RebalanceMetrics>;

interface RebalanceAccountInsightsProps {
  rebalanceAccount: RebalanceAccountWithMetrics;
  rebalanceType: RebalanceType;
  householdModelId?: number;
}

const RebalanceAccountInsights = ({
  rebalanceAccount,
  rebalanceType,
  householdModelId,
}: RebalanceAccountInsightsProps) => {
  const {
    isPending: isPendingAccount,
    isError: isErrorAccount,
    data: account,
  } = useQueryAccount(rebalanceAccount.id);

  const {
    isLoading: isLoadingModelAccount,
    isError: isErrorModelAccount,
    data: dataModelAccount,
  } = useQueryModel(account?.assignedModelId ?? -1, {
    enabled:
      typeof account !== "undefined" &&
      typeof account.assignedModelId !== "undefined",
  });

  const {
    isLoading: isLoadingModelHousehold,
    isError: isErrorModelHousehold,
    data: dataModelHousehold,
  } = useQueryModel(householdModelId ?? -1, {
    enabled:
      typeof account?.assignedModelId === "undefined" &&
      typeof householdModelId !== "undefined",
  });

  const {
    isPending: isPendingAccountAllocation,
    isError: isErrorAccountAllocation,
    data: dataAccountAllocation,
  } = useAuthenticatedFetch<
    UnpackResponse<AccountsController["getAllocationDetails"]>
  >(`/accounts/${rebalanceAccount.id}/allocation-details`, undefined, {
    enabled: true,
  });

  const buyTotal = rebalanceAccount.trades
    .filter((trade) => trade.side === "buy")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const sellTotal = rebalanceAccount.trades
    .filter((trade) => trade.side === "sell" || trade.side === "sell_all")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const realizedGainLoss =
    rebalanceAccount.realizedGainLossSt + rebalanceAccount.realizedGainLossLt;

  // Create asset allocation series for this account
  const assetAllocationSeries: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: dataAccountAllocation?.data.current ?? [],
    },
    {
      label: "Post-Rebalance",
      data: rebalanceAccount.assetAllocation ?? [],
    },
    {
      label: "Target",
      data: dataAccountAllocation?.data.target ?? [],
    },
  ];

  if (isPendingAccount) return <Loading />;
  if (isErrorAccount) return <div>An error occurred loading account</div>;
  if (!account) return <div>Account not found</div>;

  return (
    <>
      {/* Account Header */}
      <div className="account-header mb-3 pb-3 border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">
              <Link to={`/clients/accounts/${account.id}`}>
                {account.displayName} ({account.displayNumber})
              </Link>
            </h5>
            <small className="text-muted">
              {account?.type} •{" "}
              {typeof account.assignedModelId === "undefined" &&
              typeof householdModelId === "undefined" ? (
                naLabel
              ) : typeof account.assignedModelId === "undefined" ? (
                isLoadingModelHousehold ? (
                  <Loading />
                ) : isErrorModelHousehold ? (
                  <span className="text-danger">Failed to load model</span>
                ) : typeof dataModelHousehold === "undefined" ? (
                  naLabel
                ) : (
                  <Link
                    to={`/models/models/${dataModelHousehold.id}`}
                    className="font-italic"
                  >
                    {dataModelHousehold.name}
                  </Link>
                )
              ) : isLoadingModelAccount ? (
                <Loading />
              ) : isErrorModelAccount ? (
                <span className="text-danger">Failed to load model</span>
              ) : typeof dataModelAccount === "undefined" ? (
                naLabel
              ) : (
                <Link to={`/models/models/${dataModelAccount.id}`}>
                  {dataModelAccount.name}
                </Link>
              )}
            </small>
          </div>
          <div className="text-end">
            <div className="h6 mb-0">
              {formatCurrency(rebalanceAccount.totalBalance)}
            </div>
            <small className="text-muted">Total AUM</small>
            <div className="h6 mb-0 mt-2">
              {rebalanceAccount.lastTradeDate === null
                ? naLabel
                : new Date(rebalanceAccount.lastTradeDate).toLocaleDateString()}
            </div>
            <small className="text-muted">Last Trade</small>
          </div>
        </div>
      </div>

      {/* Risk Allocation */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="mb-3 d-flex align-items-end">
            <div
              style={{
                width: "120px",
                paddingRight: "12px",
                textAlign: "right",
              }}
            >
              <small className="text-muted">Current</small>
            </div>
            <div style={{ flex: "1" }}>
              <AllocationBar
                data={rebalanceAccount.riskAllocation?.current}
                converter={getRiskAllocation}
              />
            </div>
          </div>
          <div className="mb-3 d-flex align-items-end">
            <div
              style={{
                width: "120px",
                paddingRight: "12px",
                textAlign: "right",
              }}
            >
              <small className="text-muted">Post-Rebalance</small>
            </div>
            <div style={{ flex: "1" }}>
              <AllocationBar
                data={
                  rebalanceAccount.trades.length === 0
                    ? rebalanceAccount.riskAllocation?.current
                    : rebalanceAccount.riskAllocation?.postRebalance
                }
                converter={getRiskAllocation}
              />
            </div>
          </div>
          <div className="mb-3 d-flex align-items-end">
            <div
              style={{
                width: "120px",
                paddingRight: "12px",
                textAlign: "right",
              }}
            >
              <small className="text-muted">Target</small>
            </div>
            <div style={{ flex: "1" }}>
              <AllocationBar
                data={rebalanceAccount.riskAllocation?.target}
                converter={getRiskAllocation}
              />
            </div>
          </div>

          {/* Cash Flow and Trading Metrics Table */}
          <div className="mt-4">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                <tr>
                  <td
                    style={{
                      width: "60px",
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Cash
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(rebalanceAccount.cashBalance)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Sell
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(sellTotal)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Buy
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(buyTotal)}
                  </td>
                </tr>
                <tr style={{ borderTop: "1px solid #dee2e6" }}>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#495057",
                      padding: "8px 12px",
                    }}
                  >
                    Net
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      fontWeight: 500,
                      color: sellTotal - buyTotal >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(sellTotal - buyTotal)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Gains
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      color: realizedGainLoss >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(realizedGainLoss)}
                  </td>
                </tr>
                {rebalanceType === "raise_cash" ||
                rebalanceType === "spend_cash" ? (
                  <tr>
                    <td
                      style={{
                        fontWeight: 500,
                        color: "#6c757d",
                        padding: "8px 12px",
                      }}
                    >
                      {rebalanceType === "raise_cash"
                        ? "Raise Cash"
                        : "Spend Cash"}
                    </td>
                    <td className="text-end" style={{ padding: "8px 12px" }}>
                      {formatCurrency(
                        rebalanceAccount.raiseSpendCashAmount ?? 0,
                      )}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </Col>
        <Col md={6}>
          <div>
            {isPendingAccountAllocation ? (
              <Loading />
            ) : isErrorAccountAllocation ? (
              <Alert variant="danger">Failed to load asset allocation</Alert>
            ) : (
              <AssetAllocationBarChart data={assetAllocationSeries} />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default RebalanceAccountInsights;
